import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { fullFormats } from 'ajv-formats/dist/formats';
import { ajvResolver } from '@hookform/resolvers/ajv';

import { useDeepCompareMemo } from '@clatter/platform';
import {
  useCreateDocumentTemplateMutation,
  useGetDocumentTemplateByIdQuery,
  useGetProjectTemplateQueriesQuery,
  useUpdateDocumentTemplateMutation,
} from '../../../../../../services';
import { addNotification } from '../../../../../../store';
import { FolderManagerDynamicForm } from '../../../../../../components';

import { HtmlTemplateEditor } from './HtmlTemplateEditor';
import { TemplateOutputTypeHandler } from './components/TemplateOutputTypeHandler';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { routesNames } from 'apps/ng/src/app/routes';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

interface Action {
  type: string;
  payload: any;
}

interface TemplateFormProps {
  item?: any;
  workspaceData: any;
  onAction: (action: Action) => void;
}

export const DocumentTemplateForm: FC<TemplateFormProps> = ({ workspaceData, onAction }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug, itemId: templateId } = useParams();
  const { ID: workspaceId, FolderID: uploadFolderId } = workspaceData || {};

  const [isEditingMicrosite, setIsEditingMicrosite] = useState(false);
  const [selectedOutputType, setSelectedOutputType] = useState<{ label: string; value: string } | null>(null); // handles edit form visibility

  const [createTemplate, { isLoading: isCreateTemplateLoading }] = useCreateDocumentTemplateMutation();
  const [updateTemplate, { isLoading: isUpdateTemplateLoading }] = useUpdateDocumentTemplateMutation();

  const isCreating = !templateId;

  const {
    data: templateData,
    isLoading: isTemplateDataLoading,
    error: templateDataError,
  } = useGetDocumentTemplateByIdQuery(
    {
      projectTemplateId: workspaceId,
      documentTemplateId: +templateId!,
    },
    { skip: !templateId || !workspaceId },
  );

  const {
    data: queriesData,
    isLoading: isQueriesLoading,
    error: queriesError,
  } = useGetProjectTemplateQueriesQuery(
    {
      projectTemplateId: workspaceId,
    },
    { skip: !workspaceId },
  );

  const loadingError: any = templateDataError || queriesError;

  const isLoading = isTemplateDataLoading || isQueriesLoading;
  const isFormLoading = isCreateTemplateLoading || isUpdateTemplateLoading;

  const formSchemaValidation = useDeepCompareMemo(() => {
    let schema: JSONSchema = {
      type: 'object',
      properties: {
        displayName: {
          type: 'string',
          minLength: 1,
          errorMessage: {
            minLength: 'This field is required.',
          },
        },
        outputType: {
          type: 'object',
          properties: {
            label: { type: 'string' },
            value: { type: 'string' },
          },
        },
        query: {
          type: 'object',
          properties: {
            label: { type: 'string' },
            value: { type: 'number' },
          },
        },
      },
      required: [],
    };

    if (selectedOutputType?.value !== 'html' && !isCreating) {
      schema = {
        ...schema,
        properties: {
          ...schema['properties'],
          asset_id: {
            type: 'number',
          },
        },
      };
    }

    return schema;
  }, [selectedOutputType, isCreating]);

  const formSchema = useDeepCompareMemo(() => {
    let formConfig: { validation: any; fields: any[] } = {
      validation: formSchemaValidation,
      fields: [
        {
          name: 'displayName',
          type: 'string',
          inputType: 'text',
          label: 'Display Name',
        },
        {
          name: 'outputType',
          inputType: 'select',
          type: 'string',
          label: 'Output Type',
          inputOptions: {
            disabled: !isCreating,
            selectOptions: [
              { label: 'Docx', value: 'docx' },
              { label: 'Pptx', value: 'pptx' },
              { label: 'Html', value: 'html' },
            ],
          },
        },
        {
          name: 'query',
          inputType: 'select',
          type: 'string',
          label: 'Query',
          inputOptions: {
            selectOptions: (queriesData || []).map((item) => ({
              label: item?.DisplayName,
              value: item?.ID,
            })),
          },
        },
      ],
    };

    if (selectedOutputType?.value !== 'html' && !isCreating) {
      formConfig = {
        ...formConfig,
        fields: [
          ...formConfig.fields,
          {
            name: 'asset_id',
            inputType: 'hidden',
            type: 'number',
            label: 'Asset Id',
          },
        ],
      };
    }

    if (!isCreating) {
      formConfig = {
        ...formConfig,
        fields: [
          ...formConfig.fields,
          {
            name: 'schema',
            inputType: 'editor',
            label: 'Values Schema',
            inputOptions: {
              language: 'json',
              height: '420px',
            },
          },
        ],
      };
    }

    return formConfig;
  }, [templateData, queriesData, selectedOutputType, isCreating]);

  const initialValues = useDeepCompareMemo(() => {
    if (templateData?.QueryID && !templateData?.Query) {
      console.error('Error query data not found!');
    }

    let values: Record<string, any> = {
      displayName: templateData?.DisplayName || '',
      outputType: templateData?.OutputType
        ? { label: templateData?.OutputType, value: templateData?.OutputType }
        : null,
      query: templateData?.Query ? { label: templateData?.Query?.DisplayName, value: templateData?.Query?.ID } : null,
      schema: JSON.stringify(templateData?.ValuesSchema || {}, null, 2),
    };

    if (templateData?.OutputType !== 'html') {
      values = {
        ...values,
        asset_id: templateData?.AssetID || '',
      };
    }

    return values;
  }, [templateData, queriesData]);

  const customFormMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(formSchema.validation, {
      formats: fullFormats,
    }),
  });

  const { setValue, handleSubmit, reset } = customFormMethods;

  const navigateToList = useCallback(() => {
    navigate(generatePath(routesNames.workspaceTemplatesEdit, { projectId: workspaceId, slug: slug }));
  }, [workspaceId]);

  const handleFormSubmit = async (data: any) => {
    if (data === 'cancel') {
      return navigateToList();
    }

    if (isCreating && !workspaceId) {
      const message = 'Couldn\'t create the document template — workspace template data is missing.';
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );
      console.error(message);
      return;
    }

    const allowedOutputTypes = [data.outputType.value];
    const enablePdfOutput = data.outputType !== 'html' || data.outputType === 'email'; // @todo

    if (enablePdfOutput) {
      // @todo
      allowedOutputTypes.push('pdf');
    }

    let payload: any = {
      display_name: data.displayName,
      output_type: data.outputType.value,
      allowed_output_types: allowedOutputTypes,
      query_id: data.query.value,
    };

    if (selectedOutputType?.value !== 'html') {
      payload = {
        ...payload,
        asset_id: data?.asset_id || '',
      };
    }

    if (!isCreating && data.schema) {
      try {
        payload = { ...payload, values_schema: JSON.parse(data.schema) };
      } catch {
        dispatch(addNotification({ type: 'error', message: 'Values Schema contains invalid JSON.' }));
        return;
      }
    }

    let response: any = null;

    if (isCreating) {
      response = await createTemplate({ projectTemplateId: workspaceId, data: payload });
    } else {
      response = await updateTemplate({
        projectTemplateId: workspaceId,
        documentTemplateId: +templateId!,
        data: payload,
      });
    }

    if (response?.error) {
      dispatch(
        addNotification({
          type: 'error',
          message: `Couldn't save the document template "${data.displayName}". Please try again.`,
        }),
      );

      console.error(`Failed to ${isCreating ? 'create' : 'update'} document template!`, response.error);
      return;
    }

    dispatch(
      addNotification({
        type: 'success',
        message: `Successfully ${isCreating ? 'created' : 'updated'} document template: "${data.displayName}".`,
      }),
    );

    if (isCreating) {
      reset(initialValues);
    }
  };

  const handleFormChange = (form: any) => {
    if (form?.outputType && form?.outputType !== selectedOutputType) {
      setSelectedOutputType(form?.outputType);
    } else if (!form?.outputType && selectedOutputType) {
      setSelectedOutputType(null);
    }
  };

  const handleUpload = async (data: any) => {
    if (!data?.responseData?.AssetVersion?.AssetID) {
      console.error('Could not upload file no Asset ID provided!');
      return;
    }

    setValue('asset_id', data.responseData.AssetVersion.AssetID, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    handleSubmit(handleFormSubmit)();
  };

  useEffect(() => {
    reset(initialValues);
  }, [initialValues]);

  // console.log('initial values', initialValues);

  useEffect(() => {
    if (loadingError) {
      dispatch(
        addNotification({
          type: 'error',
          message: `Couldn't load the template. Please try again.`,
        }),
      );
      console.error('Loading template data error.', loadingError);
    }
  }, [loadingError]);

  return (
    <StyledContainer>
      {isEditingMicrosite ? (
        <>
          <br />
          <HtmlTemplateEditor templateData={templateData} />
        </>
      ) : (
        <>
          <FolderManagerDynamicForm
            isLoading={isLoading || !initialValues || isFormLoading}
            formSchema={formSchema}
            customFormMethods={customFormMethods}
            onChange={handleFormChange}
            onAction={handleFormSubmit}
            includeCancelButton
            hideResetButton={isCreating}
            buttonsTargetContainerId="submit-buttons"
          />

          {!isCreating && (
            <TemplateOutputTypeHandler
              uploadFolderId={uploadFolderId}
              templateData={templateData}
              isLoading={isLoading || isFormLoading}
              onEditMicrosite={() => setIsEditingMicrosite(true)}
              type={selectedOutputType}
              onUpload={handleUpload}
            />
          )}

          <div id="submit-buttons"></div>
        </>
      )}
    </StyledContainer>
  );
};
